import React, { useEffect, useState } from "react";
import { Modal, Form, Input, DatePicker, Select, message } from "antd";
import dayjs from "dayjs";
import { LinkOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";

import { CreateSlotDTO, Slot } from "../../../Modules/Slot/Model/Slot";
import { useAppSelector } from "../../../Modules/Presentation/hooks";
import { selectAccounts } from "../../../Modules/Account/Presentation/selectors";
import { selectSlots } from "../../../Modules/Slot/Presentation/selectors";
import { selectUser } from "../../../Modules/User/Presentation/selectors";
import styled from "styled-components";

const { Option } = Select;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 2px;

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-label > label {
    color: #334155;
    font-weight: 700;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-picker,
  .ant-select-selector {
    border-radius: 8px !important;
  }

  .ant-picker,
  .ant-select {
    width: 100%;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const GridField = styled.div<{ $span?: 1 | 2 }>`
  grid-column: span ${({ $span }) => $span ?? 1};

  @media (max-width: 720px) {
    grid-column: span 1;
  }
`;

const urlValidator = (_: any, value?: string) => {
  if (!value) return Promise.resolve();
  try {
    new URL(value);
    return Promise.resolve();
  } catch {
    return Promise.reject(new Error("올바른 URL 형식이 아닙니다."));
  }
};

interface Props {
  open: boolean;
  sourceSlot: Slot | null;
  onClose: () => void;
  onSubmit: (request: {
    count: number;
    slots: CreateSlotDTO[];
  }) => Promise<void> | void;
}

const SlotCloneModal: React.FC<Props> = ({
  open,
  sourceSlot,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const accounts = useAppSelector(selectAccounts);
  const slots = useAppSelector(selectSlots);
  const currentUser = useAppSelector(selectUser);

  const sellers = accounts
    .filter((a) => a.grade === "SELLER")
    .sort((a, b) => a.username.localeCompare(b.username));

  const isFullUnrestrictedAdmin =
    currentUser?.grade === "ADMIN" && !currentUser?.is_limited_partner_admin;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!open || !sourceSlot) return;

    form.setFieldsValue({
      sellerId: sourceSlot.sellerId,
      managerId: sourceSlot.manager ?? null,
      main_keyword: sourceSlot.main_keyword || "",
      word_keyword: sourceSlot.word_keyword || "-",
      mid_single: sourceSlot.mid_single || "",
      mid_compare: sourceSlot.mid_compare || "",
      product_url: sourceSlot.product_url || "",
      compare_url: sourceSlot.compare_url || "",
      memo: sourceSlot.memo || "",
      range: undefined,
      count: 1,
    });
  }, [open, sourceSlot, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleOk = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const [start, end] = values.range as [dayjs.Dayjs, dayjs.Dayjs];
      const count = Number(values.count || 1);

      const baseDto: Omit<CreateSlotDTO, "slot_number"> = {
        sellerId: values.sellerId,
        manager: values.managerId ?? null,
        main_keyword: values.main_keyword,
        word_keyword: values.word_keyword || "-",
        mid_single: values.mid_single,
        mid_compare: values.mid_compare ?? "",
        product_url: values.product_url,
        compare_url: values.compare_url ?? "",
        memo: values.memo,
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD"),
        status: "정상",
      };

      if (count === 1) {
        const nextSlotNumber = slots.length + 1;
        const slotNumberWithUuid = `${nextSlotNumber}-${uuidv4()}`;

        await onSubmit({
          count,
          slots: [
            {
              ...baseDto,
              slot_number: slotNumberWithUuid,
            },
          ],
        });
      } else {
        await onSubmit({
          count,
          slots: [
            {
              ...baseDto,
              slot_number: "",
            },
          ],
        });
      }

      form.resetFields();
      onClose();
    } catch {
      message.error("필수 항목을 다시 확인해 주세요.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="슬롯 추가"
      open={open}
      onOk={handleOk}
      okText={loading ? "추가 중..." : "추가"}
      onCancel={onClose}
      destroyOnClose
      width={900}
      confirmLoading={loading}
      bodyStyle={
        isMobile
          ? {
              maxHeight: "80vh",
              overflow: "auto",
              overscrollBehavior: "contain" as any,
            }
          : undefined
      }
    >
      <Form form={form} layout="vertical">
        <FormGrid>
          {/* 셀러 선택 */}
          <GridField>
            <Form.Item
              name="sellerId"
              label="셀러 계정"
              rules={[{ required: true, message: "셀러를 선택하세요." }]}
            >
              <Select
                placeholder="아이디 / 이름 검색"
                showSearch
                optionFilterProp="label"
                optionLabelProp="label"
                getPopupContainer={(trigger) =>
                  trigger.parentElement as HTMLElement
                }
                listHeight={isMobile ? 180 : 256}
                dropdownStyle={
                  isMobile
                    ? {
                        maxHeight: "40vh",
                        overflow: "auto",
                        overscrollBehavior: "contain" as any,
                      }
                    : undefined
                }
                dropdownMatchSelectWidth
              >
                {sellers.map((s) => (
                  <Option
                    key={s.id}
                    value={s.id}
                    label={`${s.username} (${s.name})`}
                  >
                    <div style={{ lineHeight: 1.4 }}>
                      <div>
                        <strong>ID:</strong> {s.username}
                      </div>
                      <div>
                        <strong>이름:</strong> {s.name}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </GridField>

          {/* 기간 */}
          <GridField>
            <Form.Item
              name="range"
              label="기간"
              rules={[
                { required: true, message: "기간을 선택하세요." },
                {
                  validator: (_, v) => {
                    if (!v || v.length !== 2) {
                      return Promise.reject(
                        new Error("기간을 올바르게 선택하세요."),
                      );
                    }

                    const [s, e] = v;

                    // 일반(비제한형) ADMIN만 오늘·과거 시작 허용. 제한형·그 외는 내일부터
                    if (!isFullUnrestrictedAdmin) {
                      const minStartDate = dayjs().add(1, "day").startOf("day");
                      if (s.isBefore(minStartDate)) {
                        return Promise.reject(
                          new Error("시작일은 내일부터 가능합니다."),
                        );
                      }
                    }

                    if (e.isBefore(s)) {
                      return Promise.reject(
                        new Error("종료일은 시작일과 같거나 이후여야 합니다."),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker.RangePicker
                getPopupContainer={(trigger) => document.body}
                popupClassName="slot-add-picker-dropdown"
                disabledDate={(d) => {
                  if (isFullUnrestrictedAdmin) return false;
                  return d.isBefore(
                    dayjs().add(1, "day").startOf("day"),
                    "day",
                  );
                }}
              />
            </Form.Item>
          </GridField>

          <GridField>
            <Form.Item name="main_keyword" label="메인 키워드">
              <Input placeholder="메인 키워드를 입력해주세요." />
            </Form.Item>
          </GridField>

          <GridField>
            <Form.Item
              name="count"
              label="수량"
              initialValue={1}
              rules={[
                { required: true, message: "수량을 입력하세요." },
                {
                  validator: (_, value) => {
                    if (!value || isNaN(value) || value <= 0) {
                      return Promise.reject(
                        new Error("1 이상의 숫자를 입력하세요."),
                      );
                    }
                    if (value > 100) {
                      return Promise.reject(
                        new Error("최대 100개까지 생성 가능합니다."),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input type="number" min={1} max={100} />
            </Form.Item>
          </GridField>

          <GridField>
            <Form.Item name="mid_single" label="단일 MID">
              <Input placeholder="단일 MID를 입력해주세요." />
            </Form.Item>
          </GridField>

          <GridField>
            <Form.Item
              name="mid_compare"
              label="가격비교 MID"
              rules={[{ message: "MID를 입력하세요." }]}
            >
              <Input placeholder="가격비교 MID를 입력해주세요." />
            </Form.Item>
          </GridField>

          <GridField $span={2}>
            <Form.Item
              name="product_url"
              label="상품 URL"
              rules={[
                { message: "URL을 입력하세요." },
                { validator: urlValidator },
              ]}
            >
              <Input
                prefix={<LinkOutlined />}
                placeholder="상품 URL을 입력해주세요."
              />
            </Form.Item>
          </GridField>

          <GridField $span={2}>
            <Form.Item
              name="compare_url"
              label="가격비교 URL"
              rules={[
                { message: "URL을 입력하세요." },
                { validator: urlValidator },
              ]}
            >
              <Input
                prefix={<LinkOutlined />}
                placeholder="가격비교 URL을 입력해주세요."
              />
            </Form.Item>
          </GridField>

          <GridField $span={2}>
            <Form.Item name="memo" label="메모">
              <Input.TextArea rows={3} placeholder="메모를 입력해주세요." />
            </Form.Item>
          </GridField>
        </FormGrid>
      </Form>
    </Modal>
  );
};

export default SlotCloneModal;
